import type { SubscriptionPlanDto } from '@saaga/billingv2-sdk'
import { selectTenant } from '@saaga/shared/store/slices/billing.slice'
import { useAppSelector } from '@saaga/shared/store/store'
import { trackEvent } from '@saaga/shared/utils/analytics.util'
import { pushGtmPurchase } from '@saaga/shared/utils/gtm.util'
import { logger } from '@saaga/shared/utils/logger.util'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import type { BillingInterval, BillingPlan } from '@/components/billing/billing.interface'
import { ensureFreePlanInList, transformApiPlanToBillingPlan } from '@/utils/subscription-plan.util'

import { useBillingClient } from './use-billing-client.hook'

export interface CurrentSubscription {
  planName: string | null
  status: string | null
  renewalAt: string | null
  cancelAt: string | null
  isCancelling: boolean
}

export interface UseSubscriptionReturn {
  planList: BillingPlan[]
  currentSubscription: CurrentSubscription
  isLoading: boolean
  error: string | null
  subscribe: (planName: string, isYearly: boolean) => Promise<boolean>
  cancelSubscription: () => Promise<boolean>
  isSubscribing: boolean
  billingInterval: BillingInterval
  setBillingInterval: (interval: BillingInterval) => void
}

interface SubscribeResponse {
  reactivated?: boolean
  scheduled?: boolean
  clientSecret?: string
}
interface CancelResponse {
  cancelAt?: string | Date | null
}

const handleSubscribeResponse = async (
  response: SubscribeResponse,
  refetchTenant: () => Promise<void>
): Promise<boolean> => {
  if (response.reactivated) {
    toast.success('Your subscription has been reactivated')
    await refetchTenant()
    return true
  }
  if (response.scheduled) {
    toast.success('Your plan change has been scheduled')
    await refetchTenant()
    return true
  }
  toast.success('Subscription updated successfully')
  await refetchTenant()
  return true
}

const handleCancelResponse = async (
  response: CancelResponse,
  refetchTenant: () => Promise<void>
): Promise<boolean> => {
  const msg = response.cancelAt
    ? 'Your subscription will be cancelled at the end of the billing period'
    : 'Subscription cancelled successfully'
  toast.success(msg)
  await refetchTenant()
  return true
}

const getErrorMsg = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback

const buildCurrentSubscription = (
  tenant: ReturnType<typeof selectTenant>
): CurrentSubscription => ({
  planName: tenant?.subscriptionName ?? null,
  status: tenant?.subscriptionStatus ?? null,
  renewalAt: tenant?.subscriptionRenewalAt ?? null,
  cancelAt: tenant?.subscriptionCancelAt ?? null,
  isCancelling: tenant?.subscriptionCancelAt !== null,
})

const useSubscriptionState = () => {
  const [planList, setPlanList] = useState<BillingPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isSubscribing, setIsSubscribing] = useState(false)
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('monthly')
  return {
    planList,
    setPlanList,
    isLoading,
    setIsLoading,
    error,
    setError,
    isSubscribing,
    setIsSubscribing,
    billingInterval,
    setBillingInterval,
  }
}

const useFetchPlans = (
  getPlans: () => Promise<SubscriptionPlanDto[]>,
  state: ReturnType<typeof useSubscriptionState>
) => {
  const { setIsLoading, setError, setPlanList } = state

  const fetchPlans = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      setPlanList(ensureFreePlanInList((await getPlans()).map(transformApiPlanToBillingPlan)))
    } catch (err) {
      setError(getErrorMsg(err, 'Failed to load plans'))
      logger.error('Failed to fetch subscription plans', { error: err })
    } finally {
      setIsLoading(false)
    }
  }, [getPlans, setIsLoading, setError, setPlanList])

  useEffect(() => {
    void fetchPlans()
  }, [fetchPlans])
}

interface SubscriptionActionDeps {
  subscribeApi: (planName: string, isYearly: boolean) => Promise<SubscribeResponse>
  cancelSubscriptionApi: () => Promise<CancelResponse>
  refetchTenant: () => Promise<void>
  currentPlanName: string | null
  planList: BillingPlan[]
  setIsSubscribing: (v: boolean) => void
  onClientSecret: (clientSecret: string, planName: string, isYearly: boolean) => void
}

const resolvePlanPrice = (planList: BillingPlan[], planName: string, isYearly: boolean): number => {
  const plan = planList.find((p) => p.name === planName)
  if (!plan) return 0
  return isYearly ? plan.yearlyPrice : plan.monthlyPrice
}

const trackSubscriptionSuccess = (
  planList: BillingPlan[],
  planName: string,
  isYearly: boolean
): void => {
  const value = resolvePlanPrice(planList, planName, isYearly)
  trackEvent('subscription_started', { planName, isYearly, value, currency: 'USD' })
  pushGtmPurchase({
    transactionId: `sub_${planName}_${Date.now()}`,
    value,
    currency: 'USD',
    itemList: [{ itemId: planName, itemName: `${planName} Plan`, price: value, quantity: 1 }],
  })
}

const useSubscriptionActions = (deps: SubscriptionActionDeps) => {
  const subscribe = useCallback(
    async (planName: string, isYearly: boolean): Promise<boolean> => {
      deps.setIsSubscribing(true)
      try {
        const response = await deps.subscribeApi(planName, isYearly)

        if (response.clientSecret) {
          deps.onClientSecret(response.clientSecret, planName, isYearly)
          return true
        }

        const result = await handleSubscribeResponse(response, deps.refetchTenant)
        if (result) {
          trackSubscriptionSuccess(deps.planList, planName, isYearly)
        }
        return result
      } catch (err) {
        toast.error(getErrorMsg(err, 'Failed to subscribe'))
        logger.error('Failed to subscribe', { error: err, planName, isYearly })
        return false
      } finally {
        deps.setIsSubscribing(false)
      }
    },
    [deps]
  )

  const cancelSubscription = useCallback(async (): Promise<boolean> => {
    deps.setIsSubscribing(true)
    try {
      const result = await handleCancelResponse(
        await deps.cancelSubscriptionApi(),
        deps.refetchTenant
      )
      if (result) {
        trackEvent('subscription_cancelled', { planName: deps.currentPlanName })
      }
      return result
    } catch (err) {
      toast.error(getErrorMsg(err, 'Failed to cancel subscription'))
      logger.error('Failed to cancel subscription', { error: err })
      return false
    } finally {
      deps.setIsSubscribing(false)
    }
  }, [deps])

  return { subscribe, cancelSubscription }
}

export const useSubscription = (
  onClientSecret: (clientSecret: string, planName: string, isYearly: boolean) => void
): UseSubscriptionReturn => {
  const tenant = useAppSelector(selectTenant)
  const {
    getPlans,
    subscribe: subscribeApi,
    cancelSubscription: cancelSubscriptionApi,
    refetchTenant,
  } = useBillingClient()
  const state = useSubscriptionState()

  useFetchPlans(getPlans, state)

  const { subscribe, cancelSubscription } = useSubscriptionActions({
    subscribeApi,
    cancelSubscriptionApi,
    refetchTenant,
    currentPlanName: tenant?.subscriptionName ?? null,
    planList: state.planList,
    setIsSubscribing: state.setIsSubscribing,
    onClientSecret,
  })

  return {
    planList: state.planList,
    currentSubscription: buildCurrentSubscription(tenant),
    isLoading: state.isLoading,
    error: state.error,
    subscribe,
    cancelSubscription,
    isSubscribing: state.isSubscribing,
    billingInterval: state.billingInterval,
    setBillingInterval: state.setBillingInterval,
  }
}
